import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ═══════════════════════════════════════════
   PAGE HERO — Unique split layout (not centered)
   ═══════════════════════════════════════════ */

export const PageHero = styled.section<{ $bgImage?: string }>`
  min-height: 100vh;
  background-image:
    linear-gradient(180deg,
      rgba(10, 22, 40, 0.0) 0%,
      rgba(10, 22, 40, 0.15) 45%,
      rgba(10, 22, 40, 0.8) 82%,
      var(--bg-primary) 100%),
    url(${({ $bgImage }) => $bgImage || ''});
  background-size: cover;
  background-position: center 30%;
  background-attachment: fixed;
  display: flex;
  align-items: center;
  padding: 0 1.5rem 0;

  @media (max-width: 768px) {
    background-attachment: scroll;
    min-height: 85vh;
    align-items: flex-end;
    padding-bottom: 4rem;
  }
`;

export const HeroInner = styled.div`
  max-width: var(--container-max);
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
  padding-top: 6rem;
  animation: ${fadeInUp} 0.8s ease-out;

  @media (max-width: 768px) {
    align-items: flex-start;
    text-align: left;
    padding-top: 4rem;
  }
`;

export const HeroEyebrow = styled.div`
  font-family: var(--font-heading);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: var(--accent-teal);
  margin-bottom: 1rem;
  display: inline-block;
  background: rgba(10, 22, 40, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border: 1px solid rgba(0, 201, 177, 0.3);
  padding: 0.5rem 1.4rem;
  border-radius: 100px;
`;

export const HeroTitle = styled.h1`
  font-family: var(--font-heading);
  font-size: clamp(2.4rem, 5vw, 3.8rem);
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 10.5rem;
  text-shadow: 0 4px 30px rgba(0, 0, 0, 0.5);
  max-width: 650px;

  span {
    background: var(--gradient-gold);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

export const HeroSubtitle = styled.p`
  font-size: clamp(1rem, 1.6vw, 1.15rem);
  color: var(--text-secondary);
  max-width: 520px;
  line-height: 1.8;
  margin-bottom: 2rem;
`;

export const HeroButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--gradient-gold);
  color: var(--text-inverse);
  padding: 0.9rem 2.2rem;
  border-radius: var(--border-radius);
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: all var(--transition-base);
  box-shadow: var(--shadow-glow-gold);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 30px rgba(244, 170, 0, 0.4);
    color: var(--text-inverse);
  }
`;

/* ═══════════════════════════════════════════
   SHARED
   ═══════════════════════════════════════════ */

export const SectionEyebrow = styled.div`
  font-family: var(--font-heading);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: var(--accent-teal);
  margin-bottom: 0.75rem;
`;

export const SectionTitle = styled.h2`
  font-family: var(--font-heading);
  font-size: clamp(2.2rem, 4.5vw, 3.2rem);
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 1.5rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 50px;
    height: 3px;
    background: var(--gradient-accent);
    border-radius: 2px;
  }

  &[style*="center"] {
    &::after {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

/* ═══════════════════════════════════════════
   FEATURED DESTINATION — Large hero card
   ═══════════════════════════════════════════ */

export const FeaturedSection = styled.section`
  padding: 6rem 0;
  background: var(--bg-primary);
`;

export const FeaturedContainer = styled.div`
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 1.5rem;
`;

export const FeaturedCard = styled(Link)<{ $bgImage?: string }>`
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 0;
  min-height: 480px;
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all var(--transition-base);
  text-decoration: none;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(0, 201, 177, 0.25);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4), var(--shadow-glow-teal);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    min-height: auto;
  }
`;

export const FeaturedImage = styled.div<{ $bgImage?: string }>`
  background-image: url(${({ $bgImage }) => $bgImage || ''});
  background-size: cover;
  background-position: center;
  min-height: 480px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 70%, rgba(10, 22, 40, 0.3) 100%);
  }

  @media (max-width: 768px) {
    min-height: 280px;

    &::after {
      background: linear-gradient(180deg, transparent 60%, rgba(10, 22, 40, 0.4) 100%);
    }
  }
`;

export const FeaturedContent = styled.div`
  background: var(--bg-secondary);
  padding: 3rem 2.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

export const FeaturedBadge = styled.div`
  display: inline-block;
  font-family: var(--font-heading);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--accent-gold);
  background: rgba(244, 170, 0, 0.1);
  border: 1px solid rgba(244, 170, 0, 0.25);
  padding: 0.35rem 0.9rem;
  border-radius: 100px;
  margin-bottom: 1.2rem;
  width: fit-content;
`;

export const FeaturedTitle = styled.h3`
  font-family: var(--font-heading);
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 0.8rem;
`;

export const FeaturedDescription = styled.p`
  font-size: 1rem;
  line-height: 1.8;
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
`;

export const FeaturedSpecies = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 1.5rem;
`;

export const FeaturedCta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-family: var(--font-heading);
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--accent-gold);
  transition: gap var(--transition-base);

  ${FeaturedCard}:hover & {
    gap: 0.8rem;
  }
`;

/* ═══════════════════════════════════════════
   DESTINATION GRID — Image-forward cards
   ═══════════════════════════════════════════ */

export const DestinationsSection = styled.section`
  padding: 0 0 6rem;
  background: var(--bg-primary);
`;

export const DestinationsContainer = styled.div`
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 1.5rem;
`;

export const DestinationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const DestinationCard = styled(Link)<{ $bgImage?: string }>`
  position: relative;
  display: block;
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: all var(--transition-base);
  text-decoration: none;
  min-height: 420px;

  &:hover {
    transform: translateY(-5px);
    border-color: rgba(0, 201, 177, 0.3);
    box-shadow: 0 16px 50px rgba(0, 0, 0, 0.4), var(--shadow-glow-teal);
  }

  @media (max-width: 768px) {
    min-height: 360px;
  }
`;

export const DestinationImage = styled.div<{ $bgImage?: string }>`
  position: absolute;
  inset: 0;
  background-image: url(${({ $bgImage }) => $bgImage || ''});
  background-size: cover;
  background-position: center;
  transition: transform var(--transition-slow);

  ${DestinationCard}:hover & {
    transform: scale(1.05);
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg,
      transparent 30%,
      rgba(10, 22, 40, 0.4) 55%,
      rgba(10, 22, 40, 0.92) 100%);
  }
`;

export const DestinationContent = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 420px;
  padding: 2rem;

  @media (max-width: 768px) {
    min-height: 360px;
    padding: 1.5rem;
  }
`;

export const DestinationTagline = styled.div`
  font-family: var(--font-heading);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--accent-teal);
  margin-bottom: 0.5rem;
`;

export const DestinationTitle = styled.h3`
  font-family: var(--font-heading);
  font-size: clamp(1.4rem, 2.5vw, 1.8rem);
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 0.6rem;
`;

export const DestinationDescription = styled.p`
  font-size: 0.9rem;
  line-height: 1.7;
  color: var(--text-secondary);
  margin-bottom: 1rem;
  max-width: 90%;
`;

export const DestinationSpecies = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 1rem;
`;

export const SpeciesTag = styled.span`
  font-family: var(--font-heading);
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--accent-teal);
  background: rgba(0, 201, 177, 0.1);
  border: 1px solid rgba(0, 201, 177, 0.2);
  padding: 0.25rem 0.65rem;
  border-radius: 100px;
`;

export const DestinationCta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-family: var(--font-heading);
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--accent-gold);
  transition: gap var(--transition-base);

  ${DestinationCard}:hover & {
    gap: 0.7rem;
  }
`;

/* ═══════════════════════════════════════════
   STATS STRIP — Horizontal key numbers
   ═══════════════════════════════════════════ */

export const StatsStrip = styled.section`
  padding: 3rem 0;
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
`;

export const StatsContainer = styled.div`
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 1.5rem;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  text-align: center;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
`;

export const StatItem = styled.div``;

export const StatValue = styled.div`
  font-family: var(--font-heading);
  font-size: clamp(1.8rem, 3.5vw, 2.5rem);
  font-weight: 800;
  background: var(--gradient-accent);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
`;

export const StatLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-top: 0.3rem;
`;

/* ═══════════════════════════════════════════
   CTA SECTION — Background image version
   ═══════════════════════════════════════════ */

export const CtaSection = styled.section<{ $bgImage?: string }>`
  padding: 6rem 0;
  background-image:
    linear-gradient(135deg, rgba(10, 22, 40, 0.88) 0%, rgba(15, 31, 53, 0.82) 100%),
    url(${({ $bgImage }) => $bgImage || ''});
  background-size: cover;
  background-position: center 20%;
  background-attachment: fixed;
  text-align: center;
  position: relative;

  @media (max-width: 768px) {
    background-attachment: scroll;
    background-position: center 30%;
  }

  &::before, &::after {
    content: '';
    position: absolute;
    left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--accent-gold), transparent);
    opacity: 0.25;
  }
  &::before { top: 0; }
  &::after { bottom: 0; }
`;

export const CtaContainer = styled.div`
  max-width: 700px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

export const CtaTitle = styled.h2`
  font-family: var(--font-heading);
  font-size: clamp(2rem, 4vw, 2.8rem);
  font-weight: 800;
  margin-bottom: 1rem;
  color: var(--text-primary);
`;

export const CtaSubtext = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  margin-bottom: 2rem;
  line-height: 1.7;
`;

export const CtaButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--gradient-gold);
  color: var(--text-inverse);
  padding: 1rem 2.5rem;
  border-radius: var(--border-radius);
  font-family: var(--font-heading);
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: all var(--transition-base);
  box-shadow: var(--shadow-glow-gold);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 30px rgba(244, 170, 0, 0.4);
    color: var(--text-inverse);
  }
`;
